import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { geoGraticule10, geoNaturalEarth1, geoPath } from "d3-geo";
import { feature } from "topojson-client";
import topology from "world-atlas/countries-110m.json";
import { getPublicClients } from "../services/ourClientsService";
import { loadOurClients } from "../utils/ourClientsStorage";
import "./OurClients.css";

const MAP_WIDTH = 960;
const MAP_HEIGHT = 480;
const MIN_ZOOM = 1;
const MAX_ZOOM = 4.5;

const land = feature(topology, topology.objects.countries);
const projection = geoNaturalEarth1().fitExtent(
  [
    [8, 8],
    [MAP_WIDTH - 8, MAP_HEIGHT - 8],
  ],
  { type: "Sphere" }
);
const path = geoPath(projection);

const hq = {
  label: "BLACKROTH HQ",
  coords: [78.3773, 17.4422],
};

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const getProjectedPoint = (item) => {
  const [x, y] = projection(item.coords) || [0, 0];
  return { ...item, px: x, py: y };
};

const applyViewTransform = (point, view) => ({
  ...point,
  px: point.px * view.zoom + view.x,
  py: point.py * view.zoom + view.y,
});

const mapClientRecord = (client) => ({
  id: client.id,
  client: client.client,
  city: client.city,
  cityTag: client.cityTag,
  project: client.project,
  coords: [Number(client.coords?.[0]), Number(client.coords?.[1])],
  short: client.short,
  description: client.description,
  footer: client.footer,
});

const mapLocalClientRecord = (client) => ({
  id: client.id,
  client: client.clientName,
  city: client.city || client.cityTag,
  cityTag: client.cityTag,
  project: client.projectName,
  coords: [Number(client.longitude), Number(client.latitude)],
  short: client.short,
  description: client.description,
  footer: client.footer,
});

const OurClients = () => {
  const mapRef = useRef(null);
  const [clientRecords, setClientRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [view, setView] = useState({ zoom: 1, x: 0, y: 0 });

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const data = await getPublicClients();
        setClientRecords(
          data
            .filter(
              (client) =>
                !Number.isNaN(Number(client.coords?.[0])) &&
                !Number.isNaN(Number(client.coords?.[1]))
            )
            .map(mapClientRecord)
        );
      } catch {
        setClientRecords(
          loadOurClients()
            .filter((client) => client.active !== false)
            .filter(
              (client) =>
                !Number.isNaN(Number(client.longitude)) &&
                !Number.isNaN(Number(client.latitude))
            )
            .map(mapLocalClientRecord)
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchClients();
  }, []);

  const countryPaths = useMemo(
    () => land.features.map((country) => path(country) || ""),
    []
  );
  const graticule = useMemo(() => path(geoGraticule10()) || "", []);
  const hqPoint = useMemo(() => getProjectedPoint(hq), []);
  const points = useMemo(() => clientRecords.map(getProjectedPoint), [clientRecords]);
  const hqDisplayPoint = useMemo(
    () => applyViewTransform(hqPoint, view),
    [hqPoint, view]
  );
  const displayPoints = useMemo(
    () => points.map((point) => applyViewTransform(point, view)),
    [points, view]
  );

  const activePoint = displayPoints.find((point) => point.id === activeId);

  const arcs = useMemo(
    () =>
      displayPoints.map((point) => {
        const dx = point.px - hqDisplayPoint.px;
        const dy = point.py - hqDisplayPoint.py;
        const length = Math.hypot(dx, dy) || 1;
        const midpointX = (hqDisplayPoint.px + point.px) / 2;
        const midpointY = (hqDisplayPoint.py + point.py) / 2;
        const bow = Math.min(length * 0.2, 64);

        return {
          id: point.id,
          d: `M${hqDisplayPoint.px},${hqDisplayPoint.py} Q${midpointX + (-dy / length) * bow},${
            midpointY + (dx / length) * bow
          } ${point.px},${point.py}`,
        };
      }),
    [displayPoints, hqDisplayPoint]
  );

  const updateZoom = (factor) => {
    setView((current) => {
      const nextZoom = clamp(current.zoom * factor, MIN_ZOOM, MAX_ZOOM);
      const ratio = nextZoom / current.zoom;
      const centerX = MAP_WIDTH / 2;
      const centerY = MAP_HEIGHT / 2;
      const nextX = clamp(
        centerX - (centerX - current.x) * ratio,
        MAP_WIDTH - MAP_WIDTH * nextZoom,
        0
      );
      const nextY = clamp(
        centerY - (centerY - current.y) * ratio,
        MAP_HEIGHT - MAP_HEIGHT * nextZoom,
        0
      );

      return { zoom: nextZoom, x: nextX, y: nextY };
    });
  };

  const handleWheel = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();

    const rect = mapRef.current?.getBoundingClientRect();
    if (!rect) {
      return;
    }

    const svgX = ((event.clientX - rect.left) / rect.width) * MAP_WIDTH;
    const svgY = ((event.clientY - rect.top) / rect.height) * MAP_HEIGHT;
    const factor = Math.exp(-event.deltaY * 0.0014);

    setView((current) => {
      const nextZoom = clamp(current.zoom * factor, MIN_ZOOM, MAX_ZOOM);
      const ratio = nextZoom / current.zoom;
      const nextX = clamp(
        svgX - (svgX - current.x) * ratio,
        MAP_WIDTH - MAP_WIDTH * nextZoom,
        0
      );
      const nextY = clamp(
        svgY - (svgY - current.y) * ratio,
        MAP_HEIGHT - MAP_HEIGHT * nextZoom,
        0
      );

      return { zoom: nextZoom, x: nextX, y: nextY };
    });
  }, []);

  useEffect(() => {
    const mapElement = mapRef.current;

    if (!mapElement) {
      return undefined;
    }

    mapElement.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      mapElement.removeEventListener("wheel", handleWheel);
    };
  }, [handleWheel]);

  const popupStyle = activePoint
    ? {
        left: `${clamp(
          (activePoint.px / MAP_WIDTH) * 100,
          2,
          77
        )}%`,
        top: `${clamp(
          (activePoint.py / MAP_HEIGHT) * 100,
          18,
          82
        )}%`,
      }
    : undefined;

  return (
    <main className="our-clients-page">
      <section className="clients-shell" aria-labelledby="clients-title">
        <div className="clients-heading-line">
          <span />
          <p className="clients-eyebrow">Global Engagements</p>
        </div>
        <h1 id="clients-title">Where Our Work Lands.</h1>
        <p className="clients-intro">
          {isLoading ? "Loading" : clientRecords.length} active projects across client locations - a working footprint
          across accessibility, computer vision, and healthcare operations.
        </p>

        <section className="clients-map-card" aria-label="BlackRoth client locations">
          <div className="clients-map" ref={mapRef}>
            <svg
              className="clients-world"
              viewBox={`0 0 ${MAP_WIDTH} ${MAP_HEIGHT}`}
              role="img"
              aria-label="World map of BlackRoth client engagements"
            >
              <g transform={`translate(${view.x},${view.y}) scale(${view.zoom})`}>
                <path className="client-sphere" d={path({ type: "Sphere" }) || ""} />
                <path className="client-graticule" d={graticule} />
                {countryPaths.map((countryPath, index) => (
                  <path className="client-country" d={countryPath} key={index} />
                ))}
              </g>

              {arcs.map((arc, index) => {
                const routeId = `client-route-${index}`;

                return (
                  <g
                    className={`client-route-group${activeId === arc.id ? " active" : ""}`}
                    key={arc.id}
                  >
                    <path className="client-route" d={arc.d} id={routeId} />
                    <circle className="client-route-travel" r="2.1">
                      <animateMotion
                        begin="0s"
                        dur="3.2s"
                        repeatCount="indefinite"
                        rotate="auto"
                      >
                        <mpath href={`#${routeId}`} />
                      </animateMotion>
                    </circle>
                  </g>
                );
              })}

              <g
                className="client-hq"
                transform={`translate(${hqDisplayPoint.px},${hqDisplayPoint.py})`}
              >
                <circle r="5" />
                <circle r="3" />
                <text x="8" y="3">
                  {hq.label}
                </text>
              </g>

              {displayPoints.map((point) => (
                <g
                  className={`client-marker${activeId === point.id ? " active" : ""}`}
                  key={point.id}
                  transform={`translate(${point.px},${point.py})`}
                  role="button"
                  tabIndex="0"
                  aria-label={`Show ${point.cityTag} information`}
                  onMouseEnter={() => setActiveId(point.id)}
                  onMouseMove={() => setActiveId(point.id)}
                  onMouseLeave={() => setActiveId(null)}
                  onFocus={() => setActiveId(point.id)}
                  onBlur={() => setActiveId(null)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      setActiveId(point.id);
                    }
                  }}
                >
                  <circle className="client-marker-halo" r="8" />
                  <circle className="client-marker-dot" r="3.4" />
                </g>
              ))}
            </svg>

            {activePoint && (
              <article className="client-map-popup" style={popupStyle}>
                <p>{activePoint.project}</p>
                <h2>{activePoint.client}</h2>
                <span>{activePoint.cityTag}</span>
                <strong>{activePoint.short}</strong>
              </article>
            )}

            <div className="clients-map-zoom">
              <button type="button" aria-label="Zoom in" onClick={() => updateZoom(1.35)}>
                +
              </button>
              <button type="button" aria-label="Zoom out" onClick={() => updateZoom(1 / 1.35)}>
                -
              </button>
            </div>
            <p className="clients-map-help">Scroll to zoom - tap a marker</p>
          </div>
        </section>

        <section className="clients-grid" aria-label="Client work examples">
          {clientRecords.map((client) => (
            <article
              className={`client-card${activeId === client.id ? " active" : ""}`}
              key={client.id}
              onMouseEnter={() => setActiveId(client.id)}
              onMouseLeave={() => setActiveId(null)}
            >
              <div className="client-card-header">
                <div>
                  <h2>{client.client}</h2>
                  <p>{client.project}</p>
                </div>
                <span>{client.city}</span>
              </div>
              <p className="client-description">{client.description}</p>
              <div className="client-card-footer">
                <span>{client.footer}</span>
              </div>
            </article>
          ))}
        </section>

        <aside className="clients-note">
          Prototype note - client badges shown here are typographic
          placeholders, not official logo artwork. Card copy is outcome-stated,
          company-level placeholder text pending real client sign-off/quotes.
        </aside>
      </section>
    </main>
  );
};

export default OurClients;
